import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { askInsightQuestion } from './question';
import { getPrompt } from './prompt';
import InsightsLoading from './InsightsLoading.react';
import InsightsRefresh from './InsightsRefresh';
import { useInsights } from './InsightsContext';
import { checkIfInsightsSupported } from './figureValidity';

import './insights.css';
import { getInsightsPosition } from './positioning';

const Markdown = props => {
    // eslint-disable-next-line
    return React.createElement(window['dash_core_components'].Markdown, props);
};

const MainIcon = () => {
    return (
        <div className="insights-main-icon">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
            >
                <rect x="9" y="6.5" width="8" height="9" />
                <path d="M7.941 18c-.297-1.273-1.637-2.314-2.187-3a8 8 0 1 1 12.49.002c-.55.685-1.888 1.726-2.185 2.998H7.941ZM16 20v1a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-1h8Zm-3-9.995V6l-4.5 6.005H11v4l4.5-6H13Z" />
            </svg>
        </div>
    );
};

const validFigure = figure => {
    return figure && figure.data && Boolean(figure.data.length);
};

const InsightsText = props => {
    const { currentText, insights_text, requestingInsights, insights_disable_AI_insights, error } =
        props;
    if (currentText || insights_text) {
        return <Markdown>{currentText || insights_text}</Markdown>;
    }
    if (requestingInsights) {
        return <InsightsLoading />;
    }
    if (insights_disable_AI_insights) {
        return <div className="insights-uninitialized"></div>;
    }
    if (error) {
        return null;
    }
    return (
        <div className="insights-uninitialized">
            Figure has not been initialized yet. Please wait...
        </div>
    );
};
InsightsText.propTypes = {
    insights_text: PropTypes.string,
    currentText: PropTypes.string,
    requestingInsights: PropTypes.bool,
    insights_disable_AI_insights: PropTypes.bool,
};

const Insights = props => {
    const {
        insights,
        insights_text,
        insights_conversation,
        insights_system_prompt,
        figure,
        setProps,
        toggleInsightsOpen,
    } = props;

    const insights_prompt_instructions = {
        insights_prompt: insights.insights_prompt,
        multiple_traces_prompt: insights.multiple_traces_prompt,
        question_prompt: insights.question_prompt,
        multiple_question_prompt: insights.multiple_question_prompt,
    };

    const {
        state,
        setAbortion,
        setPromptFigure,
        setError,
        setRefresh,
        setRequestNewData,
        setRequestingInsights,
        setReplying,
        setAnswer,
        setQuestionText,
        setCurrentText,
        addTextChunk,
        setDisplayText,
    } = useInsights();
    const {
        abortion,
        promptFigure,
        error,
        refresh,
        requestNewData,
        requestingInsights,
        replying,
        answer,
        questionText,
        currentText,
        displayText,
    } = state;

    const responseContainer = useRef();

    const themeClass = insights.dark_theme ? 'insights-dark-mode' : '';
    const [userHasScrolledUp, setUserHasScrolledUp] = useState(true);
    const [scrollDownOnQuestionAsked, setScrollDownOnQuestionAsked] =
        useState(false);
    const [showGradientTop, setShowGradientTop] = useState(false);
    const [showGradientBottom, setShowGradientBottom] = useState(false);
    const modalRef = useRef(null);
    const headerRef = useRef(null);
    const bodyRef = useRef(null);

    const isAIDisabled = useMemo(() => {
        return insights.disable_AI_insights && insights.disable_AI_questions;
    }, [insights.disable_AI_insights, insights.disable_AI_questions]);

    const onSubmitQuestion = useCallback(() => {
        checkIfInsightsSupported(figure, insights.SUPPORTED_TRACE_TYPES);
        const question = questionText;
        if (!questionText) {
            return;
        }
        setReplying(questionText);

        const abort = new AbortController();

        setAbortion(abort);

        setScrollDownOnQuestionAsked(true);
        askInsightQuestion(
            insights.url,
            insights_system_prompt,
            figure,
            questionText,
            insights_conversation.map(a => a.question),
            insights_prompt_instructions,
            chunk => {
                setAnswer(chunk);
            },
            abort
        )
            .then(response => {
                setAnswer('');
                setReplying('');
                setProps({
                    insights_conversation: [
                        ...insights_conversation,
                        { question, answer: response },
                    ],
                });
                setAbortion(null);
            })
            .catch(err => {
                setAnswer('');
                setReplying('');
                setError(err.toString());
                setAbortion(null);
            });
        setQuestionText('');
    }, [
        questionText,
        figure,
        insights.url,
        insights_system_prompt,
        insights_prompt_instructions,
        insights_conversation,
    ]);

    const onChangeText = useCallback(event => {
        setQuestionText(event.target.value);
    }, []);

    const onKeyUp = useCallback(
        event => {
            if (event.key === 'Enter') {
                onSubmitQuestion();
            }
        },
        [onSubmitQuestion]
    );

    const refreshInsights = useCallback(() => {
        setProps({
            insights_text: null,
            insights_system_prompt: null,
            insights_conversation: [],
        });
        setPromptFigure(null);
        setError(null);
        setRefresh(true);
    }, []);

    const onStopGenerating = useCallback(() => {
        abortion.abort();
        setAbortion(null);
        setRefresh(false);
    }, [abortion]);

    // Handle the scroll event
    const handleScroll = () => {
        const container = responseContainer.current;

        // Check if there's content above or below the current view
        const canScrollUp = container.scrollTop > 50;
        const canScrollDown =
            container.scrollHeight - container.clientHeight >
            container.scrollTop + 50;

        // Update gradient display based on scroll position
        setShowGradientTop(canScrollUp);
        setShowGradientBottom(canScrollDown);

        // Determine if the user has scrolled up, with a 20px leniency from the bottom
        setUserHasScrolledUp(
            container.scrollTop + container.clientHeight <
            container.scrollHeight - 20
        );
    };

    useEffect(() => {
        // Attach the scroll event listener
        const container = responseContainer.current;
        container.addEventListener('scroll', handleScroll);

        if (insights_system_prompt && !promptFigure) {
            setPromptFigure(figure);
        }

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Handle auto-scrolling when new content is added
    useEffect(() => {
        const container = responseContainer.current;

        // If the user hasn't scrolled up, scroll to the bottom
        if (!userHasScrolledUp || scrollDownOnQuestionAsked) {
            container.scrollTop = container.scrollHeight;
            setScrollDownOnQuestionAsked(false);
        }
    }, [answer, insights_conversation, insights_text]);

    useEffect(() => {
        if (
            insights.disable_AI_insights ||
            requestingInsights ||
            error || insights.error ||
            !validFigure(figure)
        ) {
            return;
        }
        if (!insights_system_prompt || refresh || requestNewData) {
            checkIfInsightsSupported(figure, insights.SUPPORTED_TRACE_TYPES);
            setPromptFigure(figure);
            setRequestingInsights(true);
            setRefresh(false);
            setRequestNewData(false);
            const abortController = new AbortController();
            setAbortion(abortController);
            setDisplayText('');
            getPrompt(
                insights.url,
                figure,
                prompt => setProps({ insights_system_prompt: prompt }),
                insights_prompt_instructions,
                text => {
                    addTextChunk(text);
                },
                abortController,
                refresh
            )
                .then(({text, cached}) => {
                    setDisplayText(text);
                    setCurrentText('');
                    if (!cached) {
                        setProps({insights_text: text})
                    }
                    setRequestingInsights(false);
                    setAbortion(null);
                })
                .catch(err => {
                    setRequestingInsights(false);
                    setError(err.toString());
                    setAbortion(null);
                });
        }
    }, [
        figure,
        insights_system_prompt,
        requestingInsights,
        insights.disable_AI_insights,
        insights.url,
        requestNewData,
        error,
        refresh,
    ]);

    useEffect(() => {
        const modal = modalRef.current;
        const header = headerRef.current;
        const body = bodyRef.current;

        if (!modal || !header) {
            return;
        }

        let posInitialX = 0,
            posInitialY = 0,
            posX = 0,
            posY = 0;

        const elementDrag = e => {
            e.preventDefault();
            // Calculate the new cursor position
            posX = posInitialX - e.clientX;
            posY = posInitialY - e.clientY;
            posInitialX = e.clientX;
            posInitialY = e.clientY;
            // Set the element's new position
            modal.style.top = modal.offsetTop - posY + 'px';
            modal.style.left = modal.offsetLeft - posX + 'px';
            // Reset the style incase set by initial position.
            modal.style.right = null;
            modal.style.bottom = null;
        };

        const closeDragElement = () => {
            // Stop moving when mouse button is released
            document.onmouseup = null;
            document.onmousemove = null;
        };

        const dragMouseDown = e => {
            e.preventDefault();
            // Get the initial mouse cursor position
            posInitialX = e.clientX;
            posInitialY = e.clientY;

            document.onmouseup = closeDragElement;
            document.onmousemove = elementDrag;
        };

        // Set up the event listeners
        header.onmousedown = dragMouseDown;

        const computed = window.getComputedStyle(body);
        const initialPosition = getInsightsPosition(
            insights.toggle.position,
            parseInt(computed.width.split('px')[0]),
            parseInt(computed.height.split('px')[0]),
            48
        );
        modal.style.top = initialPosition.top;
        modal.style.left = initialPosition.left;
        modal.style.right = initialPosition.right;
        modal.style.bottom = initialPosition.bottom;

        // Clean up the event listeners when the component unmounts
        // eslint-disable-next-line consistent-return
        return () => {
            header.onmousedown = null;
            document.onmouseup = null;
            document.onmousemove = null;
        };
    }, []); // Empty dependency array means this runs once on mount

    useEffect(() => {
        if (insights.error) {
            setError(insights.error);
        }
    }, [insights.error]);

    return (
        <div className={`insights ${themeClass}`} ref={modalRef}>
            <div className="ddk-eject">
                <div className="insights-body" ref={bodyRef}>
                    <div className="insights-header" ref={headerRef}>
                        <div className="insights-left">
                            {!isAIDisabled && (
                                <div className="insights-hover-container">
                                    <button
                                        className="insights-hover-btn"
                                        type="button"
                                    >
                                        Experimental AI
                                    </button>
                                    <span className="insights-hover-card">
                                        Smart Insights is experimental feature and can make mistakes.
                                        <br />
                                        Consider checking important information.
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="insights-middle">
                            <MainIcon />
                        </div>
                        <div className="insights-right">
                            {!isAIDisabled && (
                                <InsightsRefresh
                                    isFigureValid={
                                        !insights.disable_AI_insights &&
                                        validFigure(figure)
                                    }
                                    isDataChanged={figure !== promptFigure}
                                    isGenerating={!!abortion}
                                    onStopGenerating={onStopGenerating}
                                    onDataChangeRefresh={() =>
                                        setRequestNewData(true)
                                    }
                                    onRefresh={refreshInsights}
                                />
                            )}
                            <div onClick={toggleInsightsOpen}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    className="insights-hover-btn insights-exit-btn"
                                    viewBox="0 0 12 12"
                                >
                                    <path d="M2.22 2.22a.749.749 0 0 1 1.06 0L6 4.939L8.72 2.22a.749.749 0 1 1 1.06 1.06L7.061 6L9.78 8.72a.749.749 0 1 1-1.06 1.06L6 7.061L3.28 9.78a.749.749 0 1 1-1.06-1.06L4.939 6L2.22 3.28a.749.749 0 0 1 0-1.06Z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    {insights.title && insights.title.trim() && (
                        <div className="insights-title">
                            <div className="insights-title-text">
                                {insights.title}
                            </div>
                        </div>
                    )}
                    <div
                        className="insights-response-container"
                        ref={responseContainer}
                    >
                        {showGradientTop && (
                            <div className="insights-gradient-top"></div>
                        )}
                        <div className="insights-text">
                            <InsightsText
                                currentText={currentText || displayText}
                                insights_text={insights_text}
                                requestingInsights={requestingInsights}
                                insights_disable_AI_insights={
                                    insights.disable_AI_insights
                                }
                                error={error}
                            />
                            {error && (
                                <div className="insights-error">
                                    {error}
                                </div>
                            )}
                        </div>
                        <div className="insights-answers">
                            {insights_conversation.map(answer => (
                                <div className="insights-question-answer-pair">
                                    <div className="question-pair-border-top"></div>
                                    <div className="insights-answer-question insights-text">
                                        {answer.question}
                                    </div>
                                    <div className="insights-answer-reply insights-text">
                                        <MainIcon />
                                        <Markdown>{answer.answer}</Markdown>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {replying && (
                            <div className="insights-question-answer-pair">
                                <div className="question-pair-border-top"></div>
                                <div className="insights-answer-question insights-text">
                                    {replying}
                                </div>
                                <div className="insights-current-reply insights-text">
                                    <MainIcon />
                                    {answer !== '' ? (
                                        <Markdown>{answer}</Markdown>
                                    ) : (
                                        <InsightsLoading isChat={true} />
                                    )}
                                </div>
                            </div>
                        )}
                        {showGradientBottom && (
                            <div className="insights-gradient-bottom"></div>
                        )}
                    </div>
                    {!insights.disable_AI_questions && (
                        <div className="insights-question">
                            <input
                                value={questionText}
                                className="insights-question-input"
                                onChange={onChangeText}
                                placeholder="Ask a question..."
                                onKeyUp={onKeyUp}
                                onBlur={onSubmitQuestion}
                                disabled={
                                    !!replying || requestingInsights || !!error
                                }
                                type="text"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

Insights.propTypes = {
    insights: PropTypes.exact({
        title: PropTypes.string,
        disable_AI_insights: PropTypes.bool,
        disable_AI_questions: PropTypes.bool,
        insights_prompt: PropTypes.string,
        multiple_traces_prompt: PropTypes.string,
        question_prompt: PropTypes.string,
        multiple_question_prompt: PropTypes.string,
        dark_theme: PropTypes.bool,
        toggle: PropTypes.shape({
            position: PropTypes.oneOf([
                'top-left',
                'top-right',
                'top-middle',
                'bottom-left',
                'bottom-right',
            ]),
            dark_theme: PropTypes.bool,
            show_on_hover: PropTypes.bool
        }),
        url: PropTypes.string,
    }),
    toggleInsightsOpen: PropTypes.func,
    text: PropTypes.node,
    insights_conversation: PropTypes.arrayOf(
        PropTypes.shape({
            question: PropTypes.string,
            answer: PropTypes.string,
        })
    ),
    insights_system_prompt: PropTypes.string,
    figure: PropTypes.any,
    setProps: PropTypes.func,
    insights_open: PropTypes.bool,
    SUPPORTED_TRACE_TYPES: PropTypes.arrayOf(PropTypes.string),
};

export default Insights;
